import { IPromoPriceProperties } from '@vfde-brix/ws10/promo-price';
import {
    IColorSwatchItem,
    IColorSwatchProperties,
} from '@vfde-brix/ws10/color-swatch';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import { Price } from '@vfde-brix/ws10/core';
import { IHighlightBadgeProperties } from '@vfde-brix/ws10/highlight-badge';
import {
    createProductCard,
    IProductCardItemProperties,
    IProductCardProperties,
} from '@vfde-brix/ws10/product-card';
import { IFlagBadgeProperties } from '@vfde-brix/ws10/flag-badge';
import {
    AbstractedHardwareOverviewResponse,
    Color,
} from '@vfde-sails/glados-v2';
import { startAppListening } from '../app/listener';
import {
    selectDevicesToShow,
    selectNumberOfDevices,
    selectNumberOfDevicesToShow,
} from '../features/app/selectors';
import {
    RootState,
    useAppDispatch,
} from '../app/store';
import {
    selectDevice,
    selectSubscriptionId,
} from '../features/app/slice';
import { focusOnAddedDevices } from '../features/app/helpers/focusOnAddedDevices';

const mapApiCellularIconToIcon = {
    '5G': '5g',
    '5GPlus': '5g-plus',
};

/**
 * Prepares the productCard to be mounted
 */
export const prepareProductCard = (containerId: string) => {
    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectDevicesToShow(currentState) !== selectDevicesToShow(previousState),
        effect: (_action, listenerApi) => {
            mountProductCard(containerId, listenerApi.getState());
        },
    });

    startAppListening({
        predicate: (_action, currentState, previousState) =>
            // We only want to scroll to the added productCard if the user paginated,
            // not when he filtered, so only call focusOnAddedDevices when numberOfDevices did not change
            selectNumberOfDevices(currentState) !== 0
            && selectNumberOfDevices(currentState) === selectNumberOfDevices(previousState)
            && selectNumberOfDevicesToShow(currentState) !== selectNumberOfDevicesToShow(previousState),
        effect: (_action, listenerApi) => {
            const previousState = listenerApi.getOriginalState();
            const previousNumberOfDevicesToShow = selectNumberOfDevicesToShow(previousState);

            focusOnAddedDevices(previousNumberOfDevicesToShow);
        },
    });
};

/**
 * Mount Product Card
 */
const mountProductCard = (
    containerId: string,
    state: RootState,
): void => {
    const dispatch = useAppDispatch();
    const devices = selectDevicesToShow(state);
    const subscriptionId = selectSubscriptionId(state);
    const productCardContainer = document.getElementById(containerId) as HTMLElement;

    /* istanbul ignore if */
    if (!productCardContainer) {
        return;
    }

    const properties: IProductCardProperties = {
        productCardItems: [],
        business: {
            onClick: (event, productId, productUrl) => {
                event.preventDefault();
                dispatch(
                    selectDevice(
                        productId.toString(),
                        productUrl,
                    ),
                );
            },
        },
    };

    for (const device of devices!) {
        const colorSwatchProperties = getColorSwatchProperties(device.colors);
        const promoPriceProperties = getPromoPriceProperties(device, Boolean(subscriptionId));

        const highlightBadgeConfig = window[ADDITIONAL_PAGE_OPTIONS].deviceCardBadges?.[device.modelId!]?.highlightBadge;
        const flagBadgeConfig = window[ADDITIONAL_PAGE_OPTIONS].deviceCardBadges?.[device.modelId!]?.flagBadge;

        let containerHighlightBadge: IHighlightBadgeProperties | undefined;
        let containerFlagBadge: IFlagBadgeProperties | undefined;

        if (highlightBadgeConfig) {
            containerHighlightBadge = {
                optColor: highlightBadgeConfig.color,
                txtContent: highlightBadgeConfig.text,
                stdIcon: highlightBadgeConfig.icon,
                optSize: highlightBadgeConfig.size,
                business: {},
            };

            if (highlightBadgeConfig.tooltip) {
                containerHighlightBadge.containerTooltip = {
                    stdTooltipId: highlightBadgeConfig.tooltip.stdTooltipId,
                    stdHeadline: highlightBadgeConfig.tooltip.headline,
                    txtContent: highlightBadgeConfig.tooltip.text,
                    stdSizeVariant: highlightBadgeConfig.tooltip.size,
                    business: {},
                };
            }
        }

        if (flagBadgeConfig) {
            // we remove the border option here, as it is not part of the flag badge properties but it's managed in the flag badge cms object by editorial
            const { ...flagBadgeConfigWithoutBorder } = flagBadgeConfig;

            containerFlagBadge = {
                ...flagBadgeConfigWithoutBorder,
                optFullwidth: (flagBadgeConfigWithoutBorder.optFullWidth === 'true'),
                business: {},
            };
        }

        let stdCornerIcon: string | undefined;

        const optWithBorder = flagBadgeConfig?.optWithBorder === 'true';

        if (['5G', '5GPlus'].indexOf(device.connectivity!) !== -1) {
            stdCornerIcon = mapApiCellularIconToIcon[device.connectivity as keyof typeof mapApiCellularIconToIcon];
        }

        const item: IProductCardItemProperties = {
            stdProductId: parseInt(device.defaultAtomicId, 10),
            containerColorSwatch: colorSwatchProperties,
            containerPromoPrices: promoPriceProperties ? [promoPriceProperties] : [],
            linkProductUrl: device.hubpageUrl,
            containerHighlightBadge,
            containerFlagBadge,
            stdCornerIcon,
            stdHeadline: device.modelName,
            stdMoreInfoElement: window[ADDITIONAL_PAGE_OPTIONS].productCardMoreInfoElement,
            imgProductCard: device.galleryImageUrl,
            stdImageAlt: '', // empty because headline already contains the modelName
            optWithBorder,
            business: {},
        };

        if (subscriptionId) {
            item.stdDescription = `+ ${device.priceSubLevel.gross.toLocaleString('de')} &euro; ${window[ADDITIONAL_PAGE_OPTIONS].hardwareSublevelText}`;
        }

        properties.productCardItems.push(item);
    }

    createProductCard(productCardContainer, properties);
};

const getColorSwatchProperties = (availableColors: Color[]): IColorSwatchProperties => {
    const colorItems: IColorSwatchItem[] = [];

    for (const color of availableColors) {
        colorItems.push(
            {
                stdColorRgbValue: color.primaryColorRgb,
                stdColorName: color.displayLabel,
            },
        );
    }

    return {
        business: {},
        items: colorItems,
    };
};

const getPromoPriceProperties = (
    device: AbstractedHardwareOverviewResponse['devices'][number],
    hasSubscription: boolean,
): IPromoPriceProperties | null => {
    const price = Price.fromNumber(device.priceHardwareOneTimeWithTariff.gross);

    const properties: IPromoPriceProperties = {
        business: {},
        optSizeVariation: window[ADDITIONAL_PAGE_OPTIONS].promoPrice?.size,
        price,
        stdConditionAfter: window[ADDITIONAL_PAGE_OPTIONS].promoPrice?.additionalPriceText,
    };

    if (!hasSubscription) {
        properties.optPriceConditionBefore = window[ADDITIONAL_PAGE_OPTIONS].promoPrice?.priceConditionBefore;
    }

    return properties as IPromoPriceProperties;
};
